import { useState } from "react";
import SectionBg from "../components/SectionBg";
import PageHero, { HERO_IMAGES } from "../components/PageHero";
import { Link } from "react-router-dom";
import { ArrowLeft, Calculator, Percent, DollarSign, Euro, Info } from "lucide-react";

const PAIRS = [
  { label: "EUR/USD", value: "EURUSD", pipValue: 10 },
  { label: "GBP/USD", value: "GBPUSD", pipValue: 10 },
  { label: "AUD/USD", value: "AUDUSD", pipValue: 10 },
  { label: "NZD/USD", value: "NZDUSD", pipValue: 10 },
  { label: "USD/CHF", value: "USDCHF", pipValue: 10 },
  { label: "USD/CAD", value: "USDCAD", pipValue: 7.35 },
  { label: "USD/JPY", value: "USDJPY", pipValue: 6.65 },
  { label: "EUR/GBP", value: "EURGBP", pipValue: 12.5 },
  { label: "EUR/JPY", value: "EURJPY", pipValue: 6.65 },
  { label: "GBP/JPY", value: "GBPJPY", pipValue: 6.65 },
  { label: "AUD/JPY", value: "AUDJPY", pipValue: 6.65 },
  { label: "XAU/USD (Gold)", value: "XAUUSD", pipValue: 1 },
  { label: "BTC/USD", value: "BTCUSD", pipValue: 1 },
];

export default function CalcolatoreRischio() {
  const [balance, setBalance] = useState("");
  const [currency, setCurrency] = useState("USD");
  const [stopLoss, setStopLoss] = useState("");
  const [pair, setPair] = useState("EURUSD");
  const [riskMode, setRiskMode] = useState("percent");
  const [riskValue, setRiskValue] = useState("");
  const [result, setResult] = useState(null);

  const symbol = currency === "EUR" ? "€" : "$";

  const calculate = () => {
    const bal = parseFloat(balance);
    const sl = parseFloat(stopLoss);
    const risk = parseFloat(riskValue);
    if (!bal || !sl || !risk || sl <= 0) return;

    const selectedPair = PAIRS.find(p => p.value === pair);
    const pipVal = selectedPair.pipValue;
    const riskAmount = riskMode === "percent" ? (bal * risk / 100) : risk;
    const lotSize = riskAmount / (sl * pipVal);
    const miniLots = lotSize * 10;
    const microLots = lotSize * 100;

    setResult({
      riskAmount: riskAmount.toFixed(2),
      lotSize: lotSize.toFixed(2),
      miniLots: miniLots.toFixed(2),
      microLots: microLots.toFixed(2),
      pipValue: pipVal,
      riskPercent: riskMode === "percent" ? risk.toFixed(1) : ((riskAmount / bal) * 100).toFixed(2),
      pair: selectedPair.label
    });
  };

  return (
    <div className="page-container" data-testid="calcolatore-rischio-page">
      <PageHero image={HERO_IMAGES.strumenti} kicker="Risk Management" title="Calcolatore di Rischio" subtitle="Calcola i lotti in base al tuo rischio e stop loss" />
      <SectionBg type="charts" />
      <div className="container py-8 relative">
        <Link to="/strumenti" className="inline-flex items-center gap-2 mb-6 px-4 py-2 rounded-lg transition-all hover:bg-white/5" style={{ color: "var(--mm-gold)" }} data-testid="back-to-strumenti">
          <ArrowLeft size={20} />
          <span style={{ fontFamily: "'Space Grotesk', sans-serif" }}>Torna agli Strumenti</span>
        </Link>

        <div className="max-w-2xl mx-auto">
          <div className="glass-card p-6 sm:p-8">
            {/* Inputs */}
            <div className="space-y-5">
              {/* Currency */}
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>VALUTA DEL CONTO</label>
                <div className="flex gap-2">
                  {[
                    { value: "USD", label: "USD ($)", Icon: DollarSign },
                    { value: "EUR", label: "EUR (€)", Icon: Euro },
                  ].map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      onClick={() => setCurrency(value)}
                      className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
                      style={{
                        backgroundColor: currency === value ? "rgba(47, 216, 168, 0.15)" : "rgba(255,255,255,0.03)",
                        border: currency === value ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)",
                        color: currency === value ? "var(--mm-gold)" : "var(--mm-text-secondary)",
                        fontFamily: "'Space Grotesk', sans-serif"
                      }}
                      data-testid={`calc-currency-${value.toLowerCase()}`}
                    >
                      <Icon size={16} /> {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Balance */}
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>BALANCE DEL CONTO ({symbol})</label>
                <input
                  type="number"
                  value={balance}
                  onChange={(e) => setBalance(e.target.value)}
                  className="admin-input"
                  placeholder="Es. 10000"
                  data-testid="calc-balance"
                />
              </div>

              {/* Pair */}
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>CURRENCY PAIR</label>
                <select
                  value={pair}
                  onChange={(e) => setPair(e.target.value)}
                  className="admin-input"
                  data-testid="calc-pair"
                >
                  {PAIRS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                </select>
              </div>

              {/* Stop Loss */}
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>STOP LOSS (PIPS)</label>
                <input
                  type="number"
                  value={stopLoss}
                  onChange={(e) => setStopLoss(e.target.value)}
                  className="admin-input"
                  placeholder="Es. 50"
                  data-testid="calc-sl"
                />
              </div>

              {/* Risk Mode Toggle */}
              <div>
                <label className="text-sm font-semibold mb-2 block" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>RISCHIO</label>
                <div className="flex gap-2 mb-3">
                  <button
                    onClick={() => setRiskMode("percent")}
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: riskMode === "percent" ? "rgba(47, 216, 168, 0.15)" : "rgba(255,255,255,0.03)",
                      border: riskMode === "percent" ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)",
                      color: riskMode === "percent" ? "var(--mm-gold)" : "var(--mm-text-secondary)",
                      fontFamily: "'Space Grotesk', sans-serif"
                    }}
                    data-testid="calc-risk-percent"
                  >
                    <Percent size={16} /> PERCENTUALE
                  </button>
                  <button
                    onClick={() => setRiskMode("fixed")}
                    className="flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all flex items-center justify-center gap-2"
                    style={{
                      backgroundColor: riskMode === "fixed" ? "rgba(47, 216, 168, 0.15)" : "rgba(255,255,255,0.03)",
                      border: riskMode === "fixed" ? "1px solid var(--mm-gold)" : "1px solid var(--mm-border)",
                      color: riskMode === "fixed" ? "var(--mm-gold)" : "var(--mm-text-secondary)",
                      fontFamily: "'Space Grotesk', sans-serif"
                    }}
                    data-testid="calc-risk-fixed"
                  >
                    {currency === "EUR" ? <Euro size={16} /> : <DollarSign size={16} />} IMPORTO FISSO
                  </button>
                </div>
                <div>
                  <input
                    type="number"
                    value={riskValue}
                    onChange={(e) => setRiskValue(e.target.value)}
                    className="admin-input"
                    placeholder={riskMode === "percent" ? "Es. 1" : "Es. 100"}
                    data-testid="calc-risk-value"
                  />
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculate}
                className="w-full py-4 rounded-lg text-lg font-bold transition-all duration-300 hover:scale-[1.02]"
                style={{
                  backgroundColor: "var(--mm-gold)",
                  color: "#000",
                  fontFamily: "'Space Grotesk', sans-serif",
                  letterSpacing: "0.05em"
                }}
                data-testid="calc-button"
              >
                CALCOLA LOTTAGGIO
              </button>
            </div>

            {/* Results */}
            {result && (
              <div className="mt-8 space-y-4 animate-fadeIn" data-testid="calc-results">
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
                  <span className="text-sm font-semibold" style={{ color: "var(--mm-gold)", fontFamily: "'Space Grotesk', sans-serif" }}>RISULTATO</span>
                  <div className="flex-1 h-px" style={{ backgroundColor: "var(--mm-border)" }} />
                </div>

                {/* Main Result */}
                <div className="rounded-xl p-6 text-center" style={{ backgroundColor: "rgba(47, 216, 168, 0.08)", border: "2px solid rgba(47, 216, 168, 0.3)" }}>
                  <p className="text-sm mb-1" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>LOTTI STANDARD</p>
                  <p className="text-5xl sm:text-6xl font-bold gold-text" style={{ fontFamily: "'Space Grotesk', sans-serif" }} data-testid="calc-result-lots">{result.lotSize}</p>
                  <p className="text-sm mt-2" style={{ color: "var(--mm-text-secondary)" }}>{result.pair}</p>
                </div>

                {/* Details Grid */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <ResultCard label="Mini Lotti" value={result.miniLots} />
                  <ResultCard label="Micro Lotti" value={result.microLots} />
                  <ResultCard label={`Rischio ${symbol}`} value={`${symbol}${result.riskAmount}`} />
                  <ResultCard label="Rischio %" value={`${result.riskPercent}%`} />
                </div>

                {/* Info */}
                <div className="flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid var(--mm-border)" }}>
                  <Info size={16} className="flex-shrink-0 mt-0.5" style={{ color: "var(--mm-text-muted)" }} />
                  <p className="text-xs" style={{ color: "var(--mm-text-muted)" }}>
                    Pip value per lotto standard: {symbol}{result.pipValue}. Valori espressi nella valuta del conto ({currency === "EUR" ? "Euro" : "Dollari"}): sono approssimativi e possono variare in base al tasso di cambio corrente.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ResultCard({ label, value }) {
  return (
    <div className="rounded-lg p-3 text-center" style={{ backgroundColor: "rgba(255,255,255,0.03)", border: "1px solid var(--mm-border)" }}>
      <p className="text-xs mb-1" style={{ color: "var(--mm-text-muted)", fontFamily: "'Space Grotesk', sans-serif" }}>{label}</p>
      <p className="text-lg sm:text-xl font-bold" style={{ color: "var(--mm-text-primary)", fontFamily: "'Space Grotesk', sans-serif" }}>{value}</p>
    </div>
  );
}
